/**
 * Inspection-case wire schemas (M2 intake — see docs/mvp-intake-qa-spec.md).
 *
 * A case is the lifecycle entity AHEAD of field submission: ingested from a
 * client CSV feed (client-feeds.ts), confirmed with the homeowner, assigned
 * to an inspector, inspected in the field, QA'd, and finally approved for
 * upload to the CER portal. ServiceM8 stays the scheduling source of truth —
 * the case carries the SM8 job number as a cross-reference only.
 *
 * Same pattern as admin.ts/store.ts: Zod schemas here, transition matrix +
 * guard function exported alongside (mirrors ADMIN_TRANSITIONS).
 */
import { z } from 'zod';
import { InspectionTypeSchema } from './cer-portal.js';
import { FieldHardwareSchema, InspectorIdSchema } from './field.js';
import { BillingExtraInputSchema } from './invoicing.js';
import { JobCoverSchema } from './job-cover.js';
/**
 * Case lifecycle. `preConfirmed=true` cases (client already confirmed the
 * inspection with the homeowner) enter at `confirmed`; the rest enter at
 * `needs-confirmation`. `ingested` exists for feeds that land rows before
 * triage decides the confirmation path.
 */
/** Most recent distinct dashboard values, including the current value; rows use index paths. */
/** Current dashboard-owned leaves, keyed by canonical hardware row index paths. Imported/field/legacy leaves are absent. */
export const HardwareDashboardPathsSchema = z.record(z.string(), z.object({ by: z.string(), at: z.string() }));
export const HardwareValueHistorySchema = z.record(z.string(), z.array(z.unknown()).max(20));
export const JobStatusSchema = z.enum([
    'ingested',
    'needs-confirmation',
    'confirmed',
    'assigned',
    'in-field',
    'submitted',
    'qa-passed',
    'approved-for-upload',
    'uploaded',
    'closed',
    'cancelled',
]);
/** The deliberately smaller set of lifecycle buckets shown on the dashboard. */
export const DashboardJobStatusSchema = z.enum([
    'allocated',
    'ready-to-book',
    'booked',
    'inspection-completed',
    'submitted-to-cer',
    'invoiced',
    'rejected',
]);
export const DASHBOARD_JOB_STATUS_LABELS = {
    allocated: 'Allocated',
    'ready-to-book': 'Ready to Book',
    booked: 'Booked',
    'inspection-completed': 'Inspection Completed',
    'submitted-to-cer': 'Submitted to CER',
    invoiced: 'Invoiced',
    rejected: 'Rejected',
};
/** Collapse the implementation workflow into the statuses Leading Edge uses. */
export function dashboardJobStatus(status) {
    switch (status) {
        case 'ingested':
        case 'needs-confirmation':
            return 'allocated';
        case 'confirmed':
            return 'ready-to-book';
        case 'assigned':
        case 'in-field':
            return 'booked';
        case 'submitted':
        case 'qa-passed':
        case 'approved-for-upload':
            return 'inspection-completed';
        case 'uploaded':
            return 'submitted-to-cer';
        case 'closed':
            return 'invoiced';
        case 'cancelled':
            return 'rejected';
    }
}
/** Leading Edge/CER wording for persisted job status codes. */
export const JOB_STATUS_LABELS = {
    ingested: 'Allocated',
    'needs-confirmation': 'Allocated',
    confirmed: 'Ready to Book',
    assigned: 'Booked',
    'in-field': 'Booked',
    submitted: 'Inspection Completed',
    'qa-passed': 'Inspection Completed',
    'approved-for-upload': 'Approved for portal',
    uploaded: 'Submitted to CER',
    closed: 'Invoiced',
    cancelled: 'Rejected',
};
export function jobStatusLabel(status) {
    return JOB_STATUS_LABELS[status];
}
/** One history entry on a case. `from === to` is allowed for the ingest event. */
export const JobEventSchema = z.object({
    at: z.string(),
    actor: z.string(),
    from: JobStatusSchema,
    to: JobStatusSchema,
    note: z.string().optional(),
});
/** Site address, structured (the CSV feeds vary between combined/split forms). */
export const JobAddressSchema = z.object({
    line1: z.string(),
    suburb: z.string(),
    state: z.string(),
    postcode: z.string(),
});
/** Persisted result of geocoding the intake address. Coordinates use WGS84. */
export const JobLocationSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    matchedAddress: z.string().optional(),
    provider: z.enum(['amazon-location']),
    geocodedAt: z.string(),
});
/** Homeowner (or system owner) contact details from the feed. */
export const JobOwnerSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().optional(),
    phone: z.string().optional(),
});
/**
 * Whether the battery was installed concurrently with the PV system or added
 * to an existing installation. `null` = the feed said nothing.
 *
 * NOTE: this is the "when, relative to the PV" axis. It is NOT the AS/NZS
 * 5139 taxonomy (integrated BESS vs pre-assembled battery system) that picks
 * checklist Section 4 vs 5 — that lives on `FieldHardware.batteries[].systemType`
 * and no client feed currently supplies it.
 *
 * Feeds spell the "added later" case differently — the CER export says
 * `Retrofitted` — so intake normalises synonyms onto these two values rather
 * than widening the enum with per-client vocabulary.
 */
export const BatteryInstallationTypeSchema = z.enum(['concurrent', 'subsequent']);
/**
 * A party recorded against the original installation — the installing
 * electrician, or the system designer. Both arrive in the same shape and are
 * the same person on 280 of 300 rows in a typical CER tranche.
 *
 * Reference data, not inspection evidence: it prefills the cover sheet and
 * rides along on the ServiceM8 job so the office can reach whoever did the
 * work without going back to the client.
 */
export const JobPartySchema = z.object({
    name: z.string().optional(),
    /** CEC accreditation code, e.g. "S3244154". */
    accreditationCode: z.string().optional(),
    email: z.string().optional(),
    mobile: z.string().optional(),
    phone: z.string().optional(),
    /** Single-line postal address, composed at intake from the feed's parts. */
    address: z.string().optional(),
});
/** @deprecated Use {@link JobPartySchema}; kept so older imports keep resolving. */
export const JobInstallerSchema = JobPartySchema;
const InspectionJobV2Schema = z.object({
    /** Accreditation code, e.g. "BTY6303119". Unique — doubles as the store PK. */
    jobId: z.string().min(1),
    inspectionType: InspectionTypeSchema,
    /** e.g. the concurrent PVD case travelling with a BTY (and vice versa). */
    relatedCaseId: z.string().optional(),
    /** First-class Client.clientId for the organisation that allocated the work. */
    clientId: z.string().min(1),
    /** Allocation round and regional grouping; optional for legacy records. */
    trancheId: z.string().optional(),
    area: z.string().optional(),
    systemSizeKw: z.number().nonnegative().optional(),
    /** Client-supplied component details used as inspector-verifiable field prefill. */
    hardware: FieldHardwareSchema.optional(),
    hardwareMeta: z
        .object({
        editedBy: z.string(),
        editedAt: z.string(),
        history: HardwareValueHistorySchema.optional(),
        dashboardPaths: HardwareDashboardPathsSchema.optional(),
    })
        .optional(),
    /**
     * Cover sheet entered on the job, including before any field report.
     * Dashboard-owned leaves live in `coverMeta.dashboardPaths`.
     */
    cover: JobCoverSchema.optional(),
    coverMeta: z
        .object({
        editedBy: z.string(),
        editedAt: z.string(),
        history: HardwareValueHistorySchema.optional(),
        dashboardPaths: HardwareDashboardPathsSchema.optional(),
    })
        .optional(),
    /** Billing facts captured at completion; never inferred into money silently. */
    completedAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    travelKm: z.number().nonnegative().optional(),
    accommodationNights: z.number().int().nonnegative().optional(),
    billingExtras: z.array(BillingExtraInputSchema).optional(),
    revisit: z.boolean().optional(),
    billingNotes: z.string().optional(),
    /** The client's own reference from the CSV, verbatim. */
    sourceRef: z.string().optional(),
    serviceM8JobNumber: z.string().optional(),
    address: JobAddressSchema,
    location: JobLocationSchema.optional(),
    /**
     * Set when geocoding ran and found no match. Without it a permanently
     * unmatchable address stays in the pending queue forever and blocks the
     * jobs behind it, since the backfill always works from the head.
     */
    geocodeFailedAt: z.string().optional(),
    owner: JobOwnerSchema,
    installer: JobPartySchema.optional(),
    designer: JobPartySchema.optional(),
    /** YYYY-MM-DD. */
    installationDate: z.string().optional(),
    /** e.g. "SGU - Battery (Deemed)" — verbatim from the feed. */
    systemType: z.string().optional(),
    /**
     * Client free text about the installation — scope of work, kept/removed
     * equipment, battery master serials. Carried onto the ServiceM8 booking note
     * so it reaches the inspector; not parsed into structured hardware.
     */
    siteNotes: z.string().optional(),
    /**
     * The homeowner's emailed reply to the client's approach letter, verbatim.
     *
     * Kept because it is where scheduling constraints turn up — dogs, gate
     * codes, "please tell me when you're coming" — and it reaches the inspector
     * at the top of the ServiceM8 booking note. Client-owned: a re-import
     * replaces it, because their sheet is where the correspondence accumulates.
     */
    emailNotes: z.string().optional(),
    /**
     * What the client's office recorded after phoning the homeowner. Same class
     * of information as {@link emailNotes} and shown beside it, but far rarer
     * and more decisive — refusals turn up here.
     */
    callNotes: z.string().optional(),
    /**
     * Extra ways to reach the homeowner, or what the inspector needs to get on
     * site: a second mobile under someone else's name, an availability window,
     * a battery mounted at an address other than the one on the allocation.
     *
     * Distinct from {@link emailNotes} because it is the client's own note about
     * ACCESS rather than the homeowner's words, and distinct from the owner's
     * phone because it is free text — "best contact number is +61 414 213 383
     * (Sheila Chan)" names someone who is not the registered owner.
     */
    contactNotes: z.string().optional(),
    /**
     * The client's message to whoever does the inspection, verbatim. Sits with
     * the other free text at the top of the booking note.
     */
    inspectorNotes: z.string().optional(),
    /** Date the client recorded the homeowner's approval, verbatim from the feed. */
    ownerApprovalDate: z.string().optional(),
    /**
     * The client's own status word at the last import ("Pending Booking",
     * "Rejected"). Stored unmapped so a re-import can tell an actual change from
     * a vocabulary we don't recognise, and so the dashboard can show what the
     * client thinks alongside what we think.
     */
    clientStatus: z.string().optional(),
    batteryInstallationType: BatteryInstallationTypeSchema.nullable().optional(),
    /** True when the client already confirmed the inspection with the homeowner. */
    preConfirmed: z.boolean(),
    status: JobStatusSchema,
    inspectorId: InspectorIdSchema.optional(),
    /** YYYY-MM-DD. */
    scheduledDate: z.string().optional(),
    history: z.array(JobEventSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    /**
     * When the inspector submitted the field report. Set once, from the
     * submission clock or (for older jobs) the first history hop into
     * `submitted`. Review, supervisor edit, and Approve for Portal must not
     * move it. `updatedAt` changes on every write, so it is not this clock.
     */
    submittedAt: z.string().optional(),
    /**
     * When a reviewer approved the report for the portal. Cleared once the job
     * leaves `approved-for-upload`, `uploaded`, or `closed`. Not `updatedAt`.
     */
    approvedAt: z.string().optional(),
});
/**
 * Compatibility reader for records written before the terminology refactor.
 * New output contains `jobId` only; Zod strips the legacy `caseId` key.
 *
 * TODO(2026-11-03): remove the caseId fallback after all staging/test data has
 * either been copied with scripts/backfill-inspection-jobs.ts or discarded.
 */
export const InspectionJobSchema = z.preprocess((input) => {
    if (!input || typeof input !== 'object')
        return input;
    const record = input;
    if (typeof record.jobId === 'string')
        return record;
    if (typeof record.caseId === 'string')
        return { ...record, jobId: record.caseId };
    return record;
}, InspectionJobV2Schema);
const PORTAL_APPROVAL_STATUSES = new Set(['approved-for-upload', 'uploaded', 'closed']);
function historyInstant(history, to, which) {
    let found;
    for (const event of history) {
        if (event.to !== to)
            continue;
        if (found === undefined || (which === 'earliest' ? event.at < found : event.at > found)) {
            found = event.at;
        }
    }
    return found;
}
/**
 * Separate the inspector's submit instant from the reviewer's portal approval.
 *
 * Existing jobs only have `updatedAt`, which moves again on Approve for Portal,
 * so the dashboard was showing the approval time as the submit time. The first
 * history hop into `submitted` is the submit clock. The latest hop into
 * `approved-for-upload` is the approval clock, and only while the job is still
 * approved, uploaded, or closed — a send-back must not keep the old approval.
 * A value already stored on the job wins, and a stored submit time is never
 * replaced.
 *
 * Pure: nothing is written here. `writeJob` persists the result on the next
 * legitimate save, and job reads apply it so the dashboard can show the clocks
 * before that save. There is no bulk rewrite of stored jobs.
 */
export function backfillJobTimestamps(job) {
    const submittedAt = job.submittedAt ?? historyInstant(job.history, 'submitted', 'earliest');
    const approvedAt = PORTAL_APPROVAL_STATUSES.has(job.status)
        ? (job.approvedAt ?? historyInstant(job.history, 'approved-for-upload', 'latest'))
        : undefined;
    const { submittedAt: _submittedAt, approvedAt: _approvedAt, ...rest } = job;
    return {
        ...rest,
        ...(submittedAt ? { submittedAt } : {}),
        ...(approvedAt ? { approvedAt } : {}),
    };
}
/**
 * Status-transition matrix (same pattern as ADMIN_TRANSITIONS in store.ts).
 *
 * Forward path:
 *   ingested → needs-confirmation/confirmed → assigned → in-field → submitted
 *     → qa-passed → approved-for-upload → uploaded → closed
 *
 * Backward hops exist where the workflow genuinely loops:
 *   - confirmed → needs-confirmation   (confirmation fell through)
 *   - assigned → confirmed             (unassign)
 *   - in-field → assigned              (inspection abandoned / rescheduled)
 *   - submitted → in-field             (sent back to the inspector)
 *   - qa-passed / approved-for-upload → submitted (late QA catch / revoke)
 *
 * `submitted → approved-for-upload` is allowed directly — the approve-upload
 * endpoint runs QA inline, so a separate qa-passed hop is optional.
 * `cancelled` is reachable from every pre-upload status; `closed`/`cancelled`
 * are terminal.
 */
export const JOB_TRANSITIONS = {
    ingested: ['needs-confirmation', 'confirmed', 'cancelled'],
    'needs-confirmation': ['confirmed', 'cancelled'],
    confirmed: ['assigned', 'needs-confirmation', 'cancelled'],
    assigned: ['in-field', 'confirmed', 'cancelled'],
    'in-field': ['submitted', 'assigned', 'cancelled'],
    submitted: ['qa-passed', 'approved-for-upload', 'in-field', 'cancelled'],
    'qa-passed': ['approved-for-upload', 'submitted', 'cancelled'],
    'approved-for-upload': ['uploaded', 'submitted', 'cancelled'],
    uploaded: ['closed'],
    closed: [],
    cancelled: [],
};
/** Whether a manual case-status transition `from` → `to` is allowed. */
export function isAllowedJobTransition(from, to) {
    return JOB_TRANSITIONS[from].includes(to);
}
/** Entry status for a freshly ingested case (per the M2 spec). */
export function initialJobStatus(preConfirmed) {
    return preConfirmed ? 'confirmed' : 'needs-confirmation';
}
/**
 * Filter inputs accepted by GET /api/admin/cases. `limit` coerces because
 * query-string values arrive as strings (same note as AdminReportFilter).
 */
export const JobFilterSchema = z.object({
    status: JobStatusSchema.optional(),
    clientId: z.string().optional(),
    inspectorId: InspectorIdSchema.optional(),
    limit: z.coerce.number().int().positive().max(500).default(200),
});
/**
 * Body of POST /api/admin/cases/:jobId/transition.
 *
 * `inspectorId` / `scheduledDate` piggyback on the transition that needs
 * them (assign / schedule) so the admin UI does one round-trip. Transitioning
 * to `assigned` requires an inspector — either here or already on the case
 * (the route enforces the latter half).
 */
export const JobTransitionRequestSchema = z.object({
    to: JobStatusSchema,
    note: z.string().optional(),
    inspectorId: InspectorIdSchema.optional(),
    /** YYYY-MM-DD. */
    scheduledDate: z.string().optional(),
});
//# sourceMappingURL=jobs.js.map